// Combined Sign Up Page - VISUALLY UPDATED with InsureVision Colors
// Updated: Removed demo preference - all users start with intro meeting
// Updated: Added Google sign-up option
// Updated: Jan 2026 - Switched to Google redirect flow
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { n8nApi } from '../../services/n8nApi'
import PasswordInput from '../../components/PasswordInput'

const SignUpPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    companyName: '',
    phone: ''
  })
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [retryData, setRetryData] = useState(null)
  
  const { signUp, signInWithGoogle } = useAuth()
  const navigate = useNavigate()

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const validate = () => {
    const newErrors = {}
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email'
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters'
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required'
    }
    
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validate()) return
    
    setIsLoading(true)
    setErrors({})

    try {
      const user = await signUp(formData.email, formData.password)
      
      try {
        console.log('📤 Submitting signup data...')
        const response = await n8nApi.createUserWithDetails(user.uid, {
          email: formData.email,
          full_name: formData.fullName,
          company_name: formData.companyName,
          phone: formData.phone
        })

        console.log('📥 Response:', response)

        // Success if we got an array with a record, or an object with an id
        if (response && (Array.isArray(response) ? response[0]?.id : response.id || response.success)) {
          // Pass state to signal OnboardingContext that we just created a record
          navigate('/onboarding/schedule-intro', { replace: true, state: { justCreatedRecord: true } })
        } else {
          throw new Error('Failed to create user record')
        }
      } catch (airtableError) {
        console.error('⚠️ Airtable creation failed:', airtableError)
        // Store user data for retry attempt
        setRetryData({
          uid: user.uid,
          email: formData.email,
          fullName: formData.fullName,
          companyName: formData.companyName,
          phone: formData.phone
        })
        setErrors({
          submit: 'Account created, but we couldn\'t complete your profile setup. Please try again.'
        })
        // Do NOT navigate - keep user on page to retry
      }
      
    } catch (err) {
      console.error('❌ Signup error:', err)
      setErrors({ submit: getErrorMessage(err.code) })
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleSignUp = async () => {
    setErrors({})
    setIsLoading(true)
    
    try {
      const { user, isNewUser } = await signInWithGoogle()
      
      if (isNewUser) {
        // Double-check against Airtable - do they already have a record?
        try {
          const status = await n8nApi.getUserStatus(user.uid)
          if (status.found) {
            console.log('✅ User has Airtable record, not actually new')
            // Check if admin
            try {
              const adminCheck = await n8nApi.checkAdminStatus(user.uid)
              if (adminCheck.is_admin) {
                navigate('/admin')
                return
              }
            } catch (adminError) {
              console.warn('Admin check failed:', adminError)
            }
            navigate('/onboarding')
            return
          }
        } catch (e) {
          console.warn('Airtable check failed, proceeding as new user:', e)
        }
        
        navigate('/complete-profile')
        return
      }
      
      // Existing user - check if admin
      try {
        const adminCheck = await n8nApi.checkAdminStatus(user.uid)
        if (adminCheck.is_admin) {
          navigate('/admin')
          return
        }
      } catch (adminError) {
        console.warn('Admin check failed:', adminError)
      }
      
      navigate('/onboarding')
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setErrors({ submit: getGoogleErrorMessage(err.code) })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const getErrorMessage = (code) => {
    switch (code) {
      case 'auth/email-already-in-use':
        return 'An account with this email already exists.'
      case 'auth/invalid-email':
        return 'Please enter a valid email address.'
      case 'auth/operation-not-allowed':
        return 'Email/password accounts are not enabled.'
      case 'auth/weak-password':
        return 'Please choose a stronger password.'
      default:
        return 'An error occurred. Please try again.'
    }
  }

  const getGoogleErrorMessage = (code) => {
    switch (code) {
      case 'auth/popup-closed-by-user':
        return 'Sign-up cancelled. Please try again.'
      case 'auth/popup-blocked':
        return 'Pop-up blocked. Please allow pop-ups for this site.'
      case 'auth/account-exists-with-different-credential':
        return 'An account already exists with this email. Try signing in instead.'
      default:
        return 'Google sign-up failed. Please try again.'
    }
  }

  const handleRetrySetup = async () => {
    if (!retryData) return

    setIsLoading(true)
    setErrors({})

    try {
      // Check if record was actually created (in case previous attempt succeeded but response was lost)
      try {
        const existingCheck = await n8nApi.getUserStatus(retryData.uid)
        if (existingCheck.found && existingCheck.prospect) {
          console.log('✅ Record already exists, redirecting...')
          setRetryData(null)
          navigate('/onboarding/schedule-intro', { replace: true })
          return
        }
      } catch (checkErr) {
        console.warn('Pre-retry check failed, proceeding:', checkErr)
      }

      const response = await n8nApi.createUserWithDetails(retryData.uid, {
        email: retryData.email,
        full_name: retryData.fullName,
        company_name: retryData.companyName,
        phone: retryData.phone
      })

      if (response && (Array.isArray(response) ? response[0]?.id : response.id || response.success)) {
        setRetryData(null)
        // Pass state to signal OnboardingContext that we just created a record
        navigate('/onboarding/schedule-intro', { replace: true, state: { justCreatedRecord: true } })
      } else {
        throw new Error('Failed to create user record')
      }
    } catch (error) {
      console.error('⚠️ Retry failed:', error)
      setErrors({
        submit: 'Setup still failing. Please try again or contact sales@insurevision.ai'
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex flex-col items-center gap-3">
          <Link to="/">
            <img 
              src="/assets/INSUREVISION_AI_LOGO_WH.svg" 
              alt="InsureVision" 
              className="h-20"
            />
          </Link>
          <span className="font-display font-bold text-2xl text-slate-900">
            FleetVision
          </span>
        </div>
        <h2 className="mt-6 text-center text-3xl sm:text-4xl font-display font-bold text-brand-950">
          Schedule Your Demo
        </h2>
        <p className="mt-2 text-center text-sm text-slate-600">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-accent-600 hover:text-accent-700">
            Log in
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-soft rounded-2xl sm:px-10 border border-slate-200">
          {errors.submit && (
            <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-200">
              <p className="text-sm text-red-700">{errors.submit}</p>
              {retryData && (
                <button
                  type="button"
                  onClick={handleRetrySetup}
                  disabled={isLoading}
                  className="mt-3 w-full px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {isLoading ? 'Retrying...' : 'Retry Setup'}
                </button>
              )}
            </div>
          )}

          {/* Google Sign Up - Primary Option */}
          <div className="mb-6">
            <button
              type="button"
              onClick={handleGoogleSignUp}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-slate-300 rounded-xl text-slate-700 font-medium hover:bg-slate-50 hover:border-slate-400 transition-all disabled:opacity-50"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign up with Google
            </button>
          </div>

          {/* Divider */}
          <div className="mb-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-slate-500">Or sign up with email</span>
              </div>
            </div>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            {/* Account Section */}
            <div className="pb-4 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-brand-950 mb-3">Account Details</h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="label">
                    Work Email <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className={`input-field ${errors.email ? 'border-red-300' : ''}`}
                    placeholder="you@company.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="password" className="label">
                    Password <span className="text-accent-600">*</span>
                  </label>
                  <PasswordInput
                    id="password"
                    name="password"
                    autoComplete="new-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    error={!!errors.password}
                    placeholder="••••••••"
                  />
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                  )}
                  {!errors.password && (
                    <p className="mt-1 text-xs text-slate-500">Must be at least 6 characters</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="label">
                    Confirm Password <span className="text-accent-600">*</span>
                  </label>
                  <PasswordInput
                    id="confirmPassword"
                    name="confirmPassword"
                    autoComplete="new-password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    error={!!errors.confirmPassword}
                    placeholder="••••••••"
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Section */}
            <div>
              <h3 className="text-sm font-semibold text-brand-950 mb-3">Contact Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="fullName" className="label">
                    Full Name <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="fullName"
                    name="fullName"
                    type="text"
                    required
                    value={formData.fullName}
                    onChange={handleChange}
                    className={`input-field ${errors.fullName ? 'border-red-300' : ''}`}
                    placeholder="Jane Smith"
                  />
                  {errors.fullName && (
                    <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="companyName" className="label">
                    Company Name <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="companyName"
                    name="companyName"
                    type="text"
                    required
                    value={formData.companyName}
                    onChange={handleChange}
                    className={`input-field ${errors.companyName ? 'border-red-300' : ''}`}
                    placeholder="Acme Transport Ltd"
                  />
                  {errors.companyName && (
                    <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="phone" className="label">
                    Phone Number <span className="text-slate-400">(optional)</span>
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="+44 7700 900000"
                  />
                </div>
              </div>
            </div>

            {/* What happens next */}
            <div className="bg-brand-50 rounded-xl p-4 border border-brand-100">
              <h4 className="font-semibold text-brand-950 text-sm mb-2">What happens next?</h4>
              <p className="text-sm text-slate-600">
                After creating your account, you'll book a 30-minute intro call with Robert Putt 
                to discuss your fleet's risk profile and how FleetVision can help.
              </p>
            </div>

            {/* Submit */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Creating your account...
                  </span>
                ) : (
                  'Create Account & Book Intro Call'
                )}
              </button>
            </div>
          </form>

          <p className="mt-6 text-xs text-slate-500 text-center">
            By creating an account, you agree to our{' '}
            <a href="https://insurevision.ai/legal/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-accent-600 hover:text-accent-700">Privacy Policy</a>
            {' '}and{' '}
            <a href="https://insurevision.ai/legal/cookie-policy/" target="_blank" rel="noopener noreferrer" className="text-accent-600 hover:text-accent-700">Cookie Policy</a>.
          </p>
        </div>
      </div>
    </div>
  )
}

export default SignUpPage
